/**
 * EnvironmentControls - Controles de UI para los elementos de entorno del sistema solar:
 * campo de estrellas, parpadeo estelar, estructura galáctica, cinturón de asteroides,
 * cinturón de Kuiper y heliosfera.
 *
 * Extraído de UIController para reducir su tamaño y agrupar una responsabilidad cohesionada.
 * Solo depende del renderizador 3D (que aplica los cambios visuales) y del DOM (para reflejar
 * el estado en los checkboxes). No guarda estado propio más allá de la referencia al renderer.
 */
export class EnvironmentControls {
    /**
     * @param {Object} [renderer3D=null] - El renderizador 3D que aplica los cambios visuales
     */
    constructor(renderer3D = null) {
        this.renderer3D = renderer3D;
    }

    /**
     * Asigna o actualiza la referencia al renderizador 3D.
     * @param {Object} renderer3D - El renderizador 3D
     */
    setRenderer3D(renderer3D) {
        this.renderer3D = renderer3D;
    }

    /**
     * Toggle star field visibility
     * @param {boolean} visible - Whether star field should be visible
     */
    toggleStarField(visible) {
        if (!this.renderer3D) return;

        if (visible) {
            this.renderer3D.showStarField();
        } else {
            this.renderer3D.hideStarField();
        }

        // Update UI to reflect the change
        const starfieldToggle = document.getElementById('starfield-toggle');
        if (starfieldToggle) {
            starfieldToggle.checked = visible;
        }

        console.log(`Star field ${visible ? 'enabled' : 'disabled'}`);
    }

    /**
     * Toggle star twinkling effect
     * @param {boolean} enabled - Whether twinkling should be enabled
     */
    toggleStarTwinkling(enabled) {
        if (!this.renderer3D) return;

        this.renderer3D.setStarTwinkling(enabled);

        // Update UI to reflect the change
        const starTwinklingToggle = document.getElementById('star-twinkling-toggle');
        if (starTwinklingToggle) {
            starTwinklingToggle.checked = enabled;
        }

        console.log(`Star twinkling ${enabled ? 'enabled' : 'disabled'}`);
    }

    /**
     * Toggle galactic structure simulation
     * @param {boolean} enabled - Whether galactic structure should be enabled
     */
    toggleGalacticStructure(enabled) {
        if (!this.renderer3D) return;

        this.renderer3D.setGalacticStructure(enabled);

        // Update UI to reflect the change
        const galacticStructureToggle = document.getElementById('galactic-structure-toggle');
        if (galacticStructureToggle) {
            galacticStructureToggle.checked = enabled;
        }

        console.log(`Galactic structure ${enabled ? 'enabled' : 'disabled'}`);
    }

    /**
     * Toggle asteroid belt visibility
     * @param {boolean} visible - Whether asteroid belt should be visible
     */
    toggleAsteroidBelt(visible) {
        if (!this.renderer3D) return;

        if (visible) {
            this.renderer3D.showAsteroidBelt();
        } else {
            this.renderer3D.hideAsteroidBelt();
        }

        // Update UI to reflect the change
        const asteroidBeltToggle = document.getElementById('asteroid-belt-toggle');
        if (asteroidBeltToggle) {
            asteroidBeltToggle.checked = visible;
        }

        console.log(`Asteroid belt ${visible ? 'enabled' : 'disabled'}`);
    }

    /**
     * Toggle Kuiper belt visibility
     * @param {boolean} visible - Whether Kuiper belt should be visible
     */
    toggleKuiperBelt(visible) {
        if (!this.renderer3D) return;

        if (visible) {
            this.renderer3D.showKuiperBelt();
        } else {
            this.renderer3D.hideKuiperBelt();
        }

        // Update UI to reflect the change
        const kuiperBeltToggle = document.getElementById('kuiper-belt-toggle');
        if (kuiperBeltToggle) {
            kuiperBeltToggle.checked = visible;
        }

        console.log(`Kuiper belt ${visible ? 'enabled' : 'disabled'}`);
    }

    /**
     * Toggle heliosphere visibility
     * @param {boolean} visible - Whether heliosphere should be visible
     */
    toggleHeliosphere(visible) {
        if (!this.renderer3D) return;

        if (visible) {
            this.renderer3D.showHeliosphere();
        } else {
            this.renderer3D.hideHeliosphere();
        }

        // Update UI to reflect the change
        const heliosphereToggle = document.getElementById('heliosphere-toggle');
        if (heliosphereToggle) {
            heliosphereToggle.checked = visible;
        }

        console.log(`Heliosphere ${visible ? 'enabled' : 'disabled'}`);
    }
}
